import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Download, Loader2, AlertCircle, FileSpreadsheet, Image as ImageIcon } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Checkbox } from '@/components/ui/checkbox'
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { objectsApi } from '../api'
import { authHeaders } from '@/features/ai-chat/core/utils/authHeaders'

interface ObjectExportDialogProps {
    open: boolean
    onOpenChange: (open: boolean) => void
}

/**
 * Export the estate as one workbook.
 *
 * Two sheets rather than two files: the objects and the edges between them describe one
 * estate at one scenario, and handing them over separately means a consumer has to be told
 * they belong together — and can end up with a mapping whose endpoints are not in the object
 * list it was paired with.
 *
 * One scenario for the whole file. The mapping is per-scenario by definition, and the RTO and
 * RPO are read at the same scenario so the two sheets cannot describe different worlds.
 *
 * Icons are optional because they are megabytes and not everyone needs them — but anything
 * drawing this estate does, or every node is an identical box.
 */
export function ObjectExportDialog({ open, onOpenChange }: ObjectExportDialogProps) {
    const { t } = useTranslation()
    const [scenarioId, setScenarioId] = useState<string>('1')
    const [scenarios, setScenarios] = useState<Array<{ id: number; name: string }>>([])
    const [includeIcons, setIncludeIcons] = useState(false)
    const [filename, setFilename] = useState('OrchestratorEstate')
    const [busy, setBusy] = useState(false)
    const [error, setError] = useState<string | null>(null)

    useEffect(() => {
        if (!open) {
            setError(null)
            return
        }
        objectsApi.getAllScenarios()
            .then((res) => {
                const data = (res.data as any)?.data || res.data
                if (!Array.isArray(data)) return
                setScenarios(data)
                const preferred = data.find((s: any) => s.isDefault) || data[0]
                if (preferred) setScenarioId(String(preferred.id))
            })
            .catch(() => { /* the default scenario still exports */ })
    }, [open])

    // Icons turn the download into a zip, so the extension has to follow the choice.
    const extension = includeIcons ? 'zip' : 'xlsx'

    const handleExport = async () => {
        setBusy(true)
        setError(null)
        try {
            const params = new URLSearchParams({
                filename,
                scenario: scenarioId,
                icons: String(includeIcons),
            })
            const response = await fetch(`/api/objects/export?${params.toString()}`, { headers: authHeaders() })
            if (!response.ok) throw new Error(`Export failed (${response.status})`)

            // Saved through a blob rather than by navigating: the request needs the auth
            // header, which a plain link cannot carry.
            const blob = await response.blob()
            const url = URL.createObjectURL(blob)
            const a = document.createElement('a')
            a.href = url
            a.download = `${filename.replace(/\.(xlsx|zip)$/i, '')}.${extension}`
            document.body.appendChild(a)
            a.click()
            document.body.removeChild(a)
            URL.revokeObjectURL(url)
            onOpenChange(false)
        } catch (e: any) {
            setError(e?.message || 'Export failed')
        } finally {
            setBusy(false)
        }
    }

    return (
        <Dialog open={open} onOpenChange={(o) => !busy && onOpenChange(o)}>
            <DialogContent className="max-w-lg">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <Download className="h-5 w-5" />
                        {t('objects.exportDialog.title', 'Export')}
                    </DialogTitle>
                    <DialogDescription>
                        {t('objects.exportDialog.description', 'One workbook describing the estate at a chosen scenario — objects, their dependencies, and the classification taxonomy needed to draw them.')}
                    </DialogDescription>
                </DialogHeader>

                <div className="space-y-4">
                    <div className="rounded-md border bg-muted/30 p-3 space-y-2">
                        <div className="flex items-start gap-2 text-sm">
                            <FileSpreadsheet className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
                            <div>
                                <div className="font-medium">{t('objects.exportDialog.sheetObjects', 'Objects')}</div>
                                <div className="text-xs text-muted-foreground">
                                    {t('objects.exportDialog.sheetObjectsHelp', 'Every object and service — ID, description, which it is, its classification, RTO and RPO')}
                                </div>
                            </div>
                        </div>
                        <div className="flex items-start gap-2 text-sm">
                            <FileSpreadsheet className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
                            <div>
                                <div className="font-medium">{t('objects.exportDialog.sheetMapping', 'Mapping')}</div>
                                <div className="text-xs text-muted-foreground">
                                    {t('objects.exportDialog.sheetMappingHelp', 'The dependency edges between them')}
                                </div>
                            </div>
                        </div>
                        <div className="flex items-start gap-2 text-sm">
                            <FileSpreadsheet className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
                            <div>
                                <div className="font-medium">{t('objects.exportDialog.sheetClassification', 'Classification')}</div>
                                <div className="text-xs text-muted-foreground">
                                    {t('objects.exportDialog.sheetClassificationHelp', 'The taxonomy — each classification, its category, band order and colour, so a consumer can draw the same bands')}
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="space-y-2">
                        <Label htmlFor="export-scenario">{t('objects.exportDialog.scenario', 'Recovery scenario')}</Label>
                        <Select value={scenarioId} onValueChange={setScenarioId}>
                            <SelectTrigger id="export-scenario">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                {scenarios.length > 0 ? (
                                    scenarios.map((s) => (
                                        <SelectItem key={s.id} value={String(s.id)}>{s.name}</SelectItem>
                                    ))
                                ) : (
                                    <SelectItem value="1">1</SelectItem>
                                )}
                            </SelectContent>
                        </Select>
                        <p className="text-xs text-muted-foreground">
                            {t('objects.exportDialog.scenarioHelp', 'The objects and mapping sheets are read at this scenario — the dependencies differ by scenario, and so does the calculated RTO. The classification taxonomy is estate-wide.')}
                        </p>
                    </div>

                    <div className="flex items-start gap-3 rounded-md border p-3">
                        <Checkbox
                            id="export-icons"
                            checked={includeIcons}
                            onCheckedChange={(v) => setIncludeIcons(v === true)}
                            className="mt-0.5"
                        />
                        <div className="space-y-1">
                            <Label htmlFor="export-icons" className="flex cursor-pointer items-center gap-2 font-medium">
                                <ImageIcon className="h-4 w-4" />
                                {t('objects.exportDialog.includeIcons', 'Include the icons')}
                            </Label>
                            <p className="text-xs text-muted-foreground">
                                {t('objects.exportDialog.includeIconsHelp', 'Adds the icon images and a manifest saying which object uses which. The download becomes a zip holding the workbook and the images — about 6MB.')}
                            </p>
                        </div>
                    </div>

                    <div className="space-y-2">
                        <Label htmlFor="export-filename">{t('objects.exportDialog.filename', 'File name')}</Label>
                        <div className="flex items-center gap-2">
                            <Input
                                id="export-filename"
                                value={filename}
                                onChange={(e) => setFilename(e.target.value)}
                                placeholder="OrchestratorEstate"
                            />
                            <span className="text-sm text-muted-foreground">.{extension}</span>
                        </div>
                    </div>

                    {error && (
                        <div className="flex items-start gap-2 rounded-md border border-red-300 bg-red-50 p-3 text-sm text-red-700 dark:bg-red-950/30 dark:text-red-300">
                            <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
                            <span>{error}</span>
                        </div>
                    )}
                </div>

                <DialogFooter>
                    <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy}>
                        {t('common.cancel', 'Cancel')}
                    </Button>
                    <Button onClick={handleExport} disabled={busy || !filename.trim()}>
                        {busy ? (
                            <><Loader2 className="mr-2 h-4 w-4 animate-spin" />{t('objects.exportDialog.exporting', 'Exporting...')}</>
                        ) : (
                            <><Download className="mr-2 h-4 w-4" />{t('objects.exportDialog.action', 'Export')}</>
                        )}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    )
}
